import { useCallback, useEffect, useRef, useState } from "react";
import { setReasonAsker } from "@/lib/reason";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ShieldWarning } from "@phosphor-icons/react";

// Global "why are you changing this?" prompt for protected fields; resolves with the reason (≥ 5 chars) or null.
export function ReasonProvider({ children }) {
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const resolver = useRef(null);
  const ask = useCallback((fields, message) => new Promise((resolve) => { resolver.current = resolve; setText(""); setState({ fields: fields || [], message }); }), []);
  useEffect(() => { setReasonAsker(ask); return () => setReasonAsker(null); }, [ask]);
  const done = (v) => { resolver.current?.(v); resolver.current = null; setState(null); };
  const ok = text.trim().length >= 5;
  return (
    <>
      {children}
      <Dialog open={!!state} onOpenChange={(o) => { if (!o) done(null); }}>
        <DialogContent className="rounded-md" data-testid="reason-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldWarning size={18} className="text-amber-600" /> Reason for this change</DialogTitle>
            <DialogDescription>{state?.message || `You are changing ${state?.fields?.join(", ")}. Give a reason (kept in the Activity Log).`}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-1.5" data-testid="reason-fields">
            {(state?.fields || []).map((f) => <span key={f} className="rounded px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 font-mono text-[10px] uppercase tracking-wider">{f}</span>)}
          </div>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} autoFocus placeholder="e.g. LME corrected to the 10 Jun fixing as per supplier debit note DN/221" className="rounded-md text-sm" data-testid="reason-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => done(null)} data-testid="reason-cancel">Cancel</Button>
            <Button className="rounded-md" disabled={!ok} onClick={() => done(text.trim())} data-testid="reason-confirm">Save with reason</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
